<script lang="ts" setup>
import { computed, onUnmounted, ref } from 'vue'
import type { HomePageConfig } from '../configs/types'
import { xiaoyaoConfig } from '../configs/common'
import ModuleBanner from './modules/ModuleBanner.novelsprig.vue'
import ModuleGrid2x4 from './modules/ModuleGrid2x4.flipnovel.vue'
import ModuleFeatured1Plus4 from './modules/ModuleFeatured1Plus4.flipnovel.vue'
import ModuleGrid1x4 from './modules/ModuleGrid1x4.flipnovel.vue'
import ModuleFeed from './modules/ModuleFeed.flipnovel.vue'
import ModuleTags2x3 from './modules/ModuleTags2x3.flipnovel.vue'
import ModuleRanking from './modules/ModuleRanking.flipnovel.vue'
import ModuleSlideBook from './modules/ModuleSlideBook.flipnovel.vue'
import ModuleSwiper from './modules/ModuleSwiper.flipnovel.vue'
import { COLUMN_TYPE } from '@/constants'
import xiaoyaoIcon from '@/assets/xiaoyao.png'

const props = defineProps<{
  config: HomePageConfig
  setInfiniteRef: (el: HTMLElement | null) => void
  setInputRef: (el: any) => void
  input: string
  inputFocused: boolean
  pageList: any[]
  pageInfo: any
  skeleton: boolean
  modules: any[]
  currentTagId: string
  feeds: any[]
  feedShowName: string
  feedId: string
  feedsColumnType: string | number
  feedFetching: boolean
  feedEnding: boolean
  switchNavTrack: (item: any) => void
  onSearch: () => void
  gotoCollection: (id: string) => void
  gotoTags: (id: string) => void
  gotoBook: (id: string, firstChapterId?: string) => void
  getCurrentTagResources: (col: any) => any[]
  getCurrentShowTags: (col: any) => any[]
  handleTagClick: (tagId: string) => void
}>()

defineEmits<{
  (event: 'update:input', value: string): void
}>()

const progressGradient = computed(() => props.config.theme.tabActiveGradient)
const tabActiveTextColor = computed(() => props.config.theme.tabActiveColor || 'var(--un-preset-theme-colors-n-text-1)')
const stickyHeaderBg = computed(() => props.config.theme.stickyHeaderBg || 'var(--un-preset-theme-colors-n-bg-global, #fff)')
// on-banner 模式下 active tab 颜色：优先 on-banner 专用配置，其次沿用旧行为（品牌主色）
const onBannerActiveColor = computed(() => props.config.theme.onBannerTabActiveColor || props.config.theme.tabActiveColor || '#9875FB')
const onBannerInactiveColor = computed(() => props.config.theme.onBannerTabInactiveColor || 'rgba(255, 255, 255, 0.85)')
const onBannerSearchBg = computed(() => props.config.theme.onBannerSearchBg || 'rgba(255, 255, 255, 0.1)')
const onBannerSearchPlaceholderColor = computed(() => props.config.theme.onBannerSearchPlaceholderColor || 'rgba(255, 255, 255, 0.7)')
const tabActiveFontWeight = computed(() => props.config.theme.tabActiveFontWeight || '700')

const showXiaoyaoIcon = computed(() => {
  const currentHost = window.location.host
  return xiaoyaoConfig.allowedDomains.includes(currentHost)
})

// 顶部 Banner: 仅当 modules[0].columnType === BANNER 时生效
const bannerModule = computed(() => {
  const first = props.modules[0]
  return first?.columnType === COLUMN_TYPE.BANNER ? first : null
})
const restModules = computed(() => bannerModule.value ? props.modules.slice(1) : props.modules)
const hasBanner = computed(() => !!bannerModule.value)

// 滚动监听 + sticky-header 透明态切换
const scrollTop = ref(0)
const scrollContainer = ref<HTMLElement | null>(null)
// 有 Banner: 滚动超过阈值才显示常态背景；无 Banner: 常态背景始终显示
const showHeaderBg = computed(() => {
  if (!hasBanner.value)
    return true
  return scrollTop.value > 60
})

function handleScroll(e: Event) {
  const target = e.target as HTMLElement
  scrollTop.value = target.scrollTop
}

function setScrollRef(el: HTMLElement | null) {
  if (scrollContainer.value)
    scrollContainer.value.removeEventListener('scroll', handleScroll)
  scrollContainer.value = el
  if (el)
    el.addEventListener('scroll', handleScroll, { passive: true })
  props.setInfiniteRef(el)
}

onUnmounted(() => {
  if (scrollContainer.value)
    scrollContainer.value.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <div class="novelsprig-home">
    <header class="brand-heading">
      <img src="/images/novelsprig/logo.svg" alt="" width="32" height="32">
      <h1>NovelSprig</h1>
    </header>
    <div :ref="setScrollRef" class="home-content overflow-y-scroll relative" :class="{ 'home-common-tag-color': !!config.theme.commonTagText }">
      <!-- Sticky header: Search + Tab 浮在顶部 -->
      <div
        class="sticky-header"
        :class="{
          'show-bg': showHeaderBg || inputFocused,
          'on-banner': hasBanner && !showHeaderBg && !inputFocused,
        }"
      >
        <!-- Search -->
        <div class="px-15px pt-2.5 transition-all" :class="{ 'w-full!': inputFocused }">
          <div class="flex items-center space-x-5 relative z-10">
            <i-assets-icon-search class="search-icon pointer-events-none w-4 h-4 absolute left-3 top-2.5" />
            <button
              type="button"
              class="search-input !ml0 flex-1 w-full pl-9 pr-4 border-0 !outline-0 truncate text-left text-14px"
              @click="onSearch"
            >
              {{ config.texts.searchPlaceholder }}
            </button>
          </div>
          <div v-if="inputFocused" class="fixed inset-0 w-full h-100vh z-9 bg-n-bg-card" />
        </div>
        <!-- Tab -->
        <div v-show="!inputFocused" class="py-2.5 overflow-x-auto px-15px">
          <div class="flex items-center space-x-30px">
            <img v-if="showXiaoyaoIcon" :src="xiaoyaoIcon" alt="xiaoyao" class="h-24px flex-shrink-0">
            <div v-for="item in pageList" :key="item.id">
              <div class="home-tab relative" :class="{ active: item.id === pageInfo.id }" @click="switchNavTrack(item)">
                <span class="text-14px whitespace-nowrap">{{ item.showName }}</span>
                <i v-if="config.theme.showTabUnderline" class="tab-underline" />
                <span v-if="item.id === pageInfo.id && !config.theme.showTabUnderline" class="active-bar" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 顶部 Banner: 大背景，向上偏移延伸到 sticky-header 下方 -->
      <div v-if="bannerModule" v-show="!inputFocused" class="banner-wrapper">
        <ModuleBanner
          :module="bannerModule"
          :goto-book="gotoBook"
        />
      </div>

      <div class="home-bg px-15px pb-20" :class="{ 'with-banner': hasBanner }">
        <!-- Modules -->
        <div v-if="skeleton" class="h-100dvh">
          <div class="pt-20vh flex justify-center">
            <BasicSpinner class="mx-auto" />
          </div>
          <p class="mt-4 text-center font-bold">
            {{ config.texts.loading }}
          </p>
        </div>
        <div v-else v-show="!inputFocused">
          <div v-for="(col, index) in restModules" :key="col.id" :class="{ 'mt-24px': index > 0 || bannerModule }">
            <!-- 非顶部 Banner -->
            <template v-if="col.columnType === COLUMN_TYPE.BANNER">
              <div>
                <ModuleBanner
                  :module="col"
                  :goto-book="gotoBook"
                />
              </div>
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.SLIDE_BOOK">
              <ModuleSlideBook
                :module="col"
                :config="config"
                :goto-book="gotoBook"
                :goto-collection="gotoCollection"
                card-class="p-0"
                header-class="flex items-center justify-between"
              />
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_3">
              <ModuleTags2x3
                :module="col"
                :config="config"
                :current-tag-id="currentTagId"
                :goto-book="gotoBook"
                :goto-tags="gotoTags"
                :get-current-tag-resources="getCurrentTagResources"
                :get-current-show-tags="getCurrentShowTags"
                :handle-tag-click="handleTagClick"
                card-class="p-0"
                header-class="flex items-center justify-between"
              />
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_PLUS_COL_4">
              <ModuleFeatured1Plus4
                :module="col"
                :config="config"
                :goto-book="gotoBook"
                :goto-collection="gotoCollection"
                card-class="p-0"
                header-class="flex items-center justify-between"
              />
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_4">
              <ModuleGrid2x4
                :module="col"
                :config="config"
                :goto-book="gotoBook"
                :goto-collection="gotoCollection"
                card-class="p-0"
                header-class="flex items-center justify-between"
              />
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_MUL_COL_4">
              <ModuleGrid1x4
                :module="col"
                :config="config"
                :goto-book="gotoBook"
                :goto-collection="gotoCollection"
                card-class="p-0"
                header-class="flex items-center justify-between"
              />
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.ROW_3_MUL_COL_4">
              <ModuleRanking
                :module="col"
                :config="config"
                card-class="p-0"
                header-class="flex items-center justify-between"
              />
            </template>
            <template v-else-if="col.columnType === COLUMN_TYPE.SWIPER">
              <ModuleSwiper
                :module="col"
                :config="config"
                card-class="p-0"
                header-class="flex items-center justify-between"
              />
            </template>
          </div>
        </div>

        <!-- Feed -->
        <ModuleFeed
          v-if="feeds.length"
          :key="pageInfo.id"
          card-class="p-0 mt-6"
          :feeds="feeds"
          :feed-show-name="feedShowName"
          :feed-id="feedId"
          :feeds-column-type="feedsColumnType"
          :feed-fetching="feedFetching"
          :feed-ending="feedEnding"
          :config="config"
          :goto-book="gotoBook"
        />
      </div>

      <BasicTabbar v-show="!inputFocused" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Sticky header: 浮在顶部 */
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: transparent;
  transition: background-color 0.3s ease;
}

.sticky-header.show-bg {
  background: v-bind(stickyHeaderBg);
}

/* Tab */
.home-tab {
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}

.home-tab span {
  color: var(--un-preset-theme-colors-n-text-3);
  transition: color 0.3s ease;
}

.home-tab.active span {
  position: relative;
  line-height: 20px;
  font-size: 16px;
  color: v-bind(tabActiveTextColor);
  font-weight: v-bind(tabActiveFontWeight);
  z-index: 1;
}

.home-tab.active i {
  position: absolute;
  left: 0;
  bottom: 0;
  margin-bottom: 2px;
  width: 100%;
  height: 6px;
  background-image: v-bind(progressGradient);
  border-radius: 3px;
  z-index: 0;
}

.home-tab.active .tab-underline {
  display: block;
  position: static;
  width: calc(100% - 18px);
  max-width: 50px;
  height: 4px;
  background: v-bind(tabActiveTextColor);
  border-radius: 3px;
  margin-top: 4px;
}

/* active tab 小横线（UI 规范：18×3 圆角 6px，颜色用品牌主色） */
.home-tab.active .active-bar {
  display: block;
  width: 18px;
  height: 3px;
  background: v-bind(tabActiveTextColor);
  border-radius: 6px;
  margin: 4px auto 0;
}

/* Search */
.search-input {
  border-radius: 24px;
  background: var(--un-preset-theme-colors-n-bg-card);
  color: var(--un-preset-theme-colors-n-text-1);
  transition: background 0.3s ease, color 0.3s ease;
}

/* show-bg：纯白背景（滚动后） */
.sticky-header.show-bg .search-input {
  background: #ffffff;
}

.search-icon {
  color: var(--un-preset-theme-colors-n-text-2);
  transition: color 0.3s ease;
}

/* on-banner（在 banner 大背景上）：非 active Tab 文字为白色 */
.sticky-header.on-banner .home-tab span {
  color: v-bind(onBannerInactiveColor);
}

/* on-banner 模式下 active Tab 保留品牌主色（与 UI 一致） */
.sticky-header.on-banner .home-tab.active span {
  color: v-bind(onBannerActiveColor);
}

.sticky-header.on-banner .home-tab.active .tab-underline {
  background: v-bind(onBannerActiveColor);
}

.sticky-header.on-banner .search-input {
  background: v-bind(onBannerSearchBg);
  color: #ffffff;
}

.sticky-header.on-banner .search-input::placeholder {
  color: v-bind(onBannerSearchPlaceholderColor);
}

/* on-banner 态 active-bar 跟随 on-banner 选中色（原先只有 tab-underline 跟随） */
.sticky-header.on-banner .home-tab.active .active-bar {
  background: v-bind(onBannerActiveColor);
}

.sticky-header.on-banner .search-icon {
  color: rgba(255, 255, 255, 0.8);
}

/* Banner-wrapper: 向上偏移，让 banner 延伸到 sticky-header 下方
   留足 116px 余量与 banner-top-mask 高度对齐，确保 banner 顶部不会在 sticky-header 上方露出 */
.banner-wrapper {
  position: relative;
  margin-top: -116px;
  z-index: 0;
}

/* home-bg：modules 容器；不再使用 home-bg 装饰背景图，
   避免与 sticky-header 渐变出现断层。整体页面浅色背景由 body 的 bgGlobal 承接，
   与 sticky-header 渐变的结束色（n-bg-global）无缝衔接。 */
.home-bg {
  position: relative;
  z-index: 1;
}

.home-bg.with-banner {
  /* 向上偏移，盖住 banner 底部一小段，与 banner 底部圆角呼应 */
  margin-top: -36px;
  z-index: 2;
}

.novelsprig-home {
  height: 100dvh;
  background: #ECFDF5;
}

.brand-heading {
  height: 48px;
  padding: 8px 15px 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;

  h1 {
    margin-left: 5px;
    color: #1F2937;
    font-size: 25px;
    font-weight: 800;
    letter-spacing: -1px;
    line-height: 32px;
  }
}

.home-content {
  height: calc(100dvh - 48px);

  :deep(.sticky-header) { background: #ECFDF5; }
  :deep(.search-input) {
    height: 42px;
    border: 1px solid #A7F3D0;
    border-radius: 12px;
    background: #FFFFFF;
    color: #1F2937;
  }
  :deep(.search-input::placeholder) { color: #4B5563; }
  :deep(.sticky-header.on-banner .search-input) { color: #1F2937; }
  :deep(.sticky-header.on-banner .search-icon) { color: #4B5563; }
  :deep(.search-icon) { color: #4B5563; top: 13px; }
  :deep(.home-tab span) { color: #4B5563; }
  :deep(.home-tab.active span) { color: #10B981; }
  :deep(.banner-wrapper) {
    margin: 8px 15px 0;
    border-radius: 20px;
    overflow: hidden;
  }
  :deep(.banner-top-mask) { display: none; }
  :deep(.home-bg.with-banner) { margin-top: 0; }

}
</style>
